/** @format */

import React, {useCallback, useEffect, useState} from 'react';
import { TextField }                             from '@material-ui/core';
import I18n from '../../components/Translate/I18n';
import { FormGeneralHelper } from '../../helpers/FormGeneral';
import { ItemLoaders } from '../../core/loaders/ItemLoaders';
import { configDefault } from '../../core/config';
import { useDispatch } from 'react-redux';
import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import Card from '../../components/Card/Card';
import CardHeader from '../../components/Heading/CardHeaderExt';
import CardBody from '../../components/Card/CardBody';
import DashboardIcon from '@material-ui/icons/Dashboard';
import { serviceCodeConstants } from '../../modules/servicecode/reducers';
import CustomTextButtom from '../../components/Buttons/CustomTextButton';
import {PlatformResellerCollectionInterface} from "../../interfaces/collections/PlatformResellerCollectionInterface";
import {platformResellerConstants} from "../../modules/platformreseller/actions";
import SelectFromRest from "../../components/Form/SelectFromRest";

interface ServiceCodeFormInterface {
    text: string;
    description?: string | null;
    platformResellerId: number;
}

interface ServiceCodeFormErrorsInterface {
    text: boolean;
    description?: boolean;
    platformResellerId: boolean;
}

export const ServiceCodeAdd = (): React.ReactElement => {
    const PLATFORM_RESELLER_URI = '/platform_resellers';

    const dispatch = useDispatch();

    const [serviceCodeForm, setServiceCodeForm] = useState<ServiceCodeFormInterface>({ text: '', description: '', platformResellerId: -1 });
    const [serviceCodeFormErrors, setServiceCodeFormErrors] = useState<ServiceCodeFormErrorsInterface>({ text: false, platformResellerId: false });

    const [platformResellerList, setPlatformResellerList] = useState<PlatformResellerCollectionInterface | null>(null);

    const changePlatformResellerId = (e: any, stateName: string, require: boolean): void => {
        if (stateName === 'platformResellerId' ) {
            setServiceCodeForm({...serviceCodeForm, platformResellerId: e.target.value });
        }
    }

    const loadPlatformResellers = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(PLATFORM_RESELLER_URI, configDefault.PLATFORM_API_PREFIX);
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformResellerConstants.PLATFORM_RESELLER_LIST,
                [{ name: 'itemsPerPage', value: 9999 }],
                [],
                (data: PlatformResellerCollectionInterface) => {
                    setPlatformResellerList(data);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch]
    );


    const saveServiceCode = (): void => {
        const itemLoaders = new ItemLoaders(dispatch);
        const isValid = isFormValid();
        if (isValid) {
            itemLoaders.postWithCallback(
                FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/service_codes', configDefault.PLATFORM_API_PREFIX)),
                serviceCodeConstants.SERVICE_CODE_ADD,
                serviceCodeForm,
                () => {
                    setServiceCodeFormErrors({ text: false, platformResellerId: false });
                },
                {
                    propagateDataToCallback: false,
                    dispatchAfterCallback: false,
                }
            );
        } else {
            setServiceCodeFormErrors({ text: true, platformResellerId: false });
        }
    };

    const editFormHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const name = event.target.name;
        const value = event.target.value;
        setServiceCodeForm({
            ...serviceCodeForm,
            [name]: value,
        });
    };

    const isFormValid = (): boolean => {
        if (serviceCodeForm.text.trim().length <= 0) {
            setServiceCodeFormErrors({ ...serviceCodeFormErrors, text: false });
            return false;
        } else {
            setServiceCodeFormErrors({ ...serviceCodeFormErrors, text: true });
            return true;
        }
    };

    useEffect((): void => {
        loadPlatformResellers();
    }, [loadPlatformResellers]);

    if(!platformResellerList) {
        return <div>Loading...</div>;
    }

    return (
        <GridContainer>
            <GridItem xs={12}>
                <Card>
                    <CardHeader icon={<DashboardIcon />} trans="service_code:h.add_service_code" />
                    <CardBody>
                        <GridContainer>
                            <GridItem xs={4}>
                                <TextField
                                    error={serviceCodeFormErrors.text}
                                    label={<I18n trans={'service_code:f.text'} />}
                                    required
                                    style={{ margin: 10 }}
                                    onChange={editFormHandler}
                                    name={'text'}
                                    helperText={serviceCodeFormErrors.text && <I18n trans={'service_code:err.text_error'} />}
                                />
                                <TextField
                                    label={<I18n trans={'service_code:f.description'} />}
                                    style={{ margin: 10 }}
                                    onChange={editFormHandler}
                                    name={'description'}
                                />
                                <SelectFromRest
                                    callback={changePlatformResellerId}
                                    required={true}
                                    addEmpty={false}
                                    restElements={platformResellerList ?? { 'hydra:member': [] }}
                                    stateName={'platformResellerId'}
                                    stateObjectState={'platformResellerId'}
                                    selectedValue={serviceCodeForm.platformResellerId}
                                    propKey="id"
                                    propValue="mainname"
                                    displayEmpty={true}
                                    emptyLabel={'services_activation:h.select_missing'}
                                    label={'services_activation:form.select_partner'}
                                />
                            </GridItem>
                            <GridItem xs={8} />
                            <GridItem xs={12}>
                                <CustomTextButtom
                                    size={'sm'}
                                    callback={saveServiceCode}
                                    callbackParam={{}}
                                    label="service_code:b.add_new_service_code"
                                />
                            </GridItem>
                        </GridContainer>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};
